import Vue from 'vue'
import store from '@/store'
import Router from 'vue-router'
import RouterQueryParameter from '@/router/QueryParameter'
import Hello from '@/components/Hello'
import Welcome from '@/components/Welcome'
import AboutMe from '@/components/AboutMe'
import Login from '@/components/Login'
import Entity from '@/components/Entity'
import EntityCreate from '@/components/EntityCreate'
import EntityEdit from '@/components/EntityEdit'
import EntityListView from '@/components/EntityListView'
import Public from '@/components/Public'
import PublicEntity from '@/components/PublicEntity'
import Upload from '@/components/Upload'
import Help from '@/components/Help'
import NotFound from '@/components/NotFound'

import schema from '@/schema'
import { Dialog } from 'buefy'

Vue.use(Router)

function createRoutes (leurisMode) {
  let routes = []

  if (leurisMode === 'FOB') {
    routes.push({
      path: '/',
      name: 'start',
      component: Hello
    })
    routes.push({
      path: '/login',
      name: 'login',
      component: Login
    })
    routes.push({
      path: '/meine-aktivitaeten',
      name: 'aboutme',
      component: AboutMe,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/eintragen/:resourceType?',
      name: 'eintragen',
      component: EntityCreate,
      props: true,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/bearbeiten/:resourceType?/:resourceId?',
      name: 'editieren',
      component: EntityEdit,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/upload',
      name: 'upload',
      component: Upload,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/zeige',
      name: 'anzeigen',
      component: Entity,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/zeige/:resourceType/:resourceId',
      name: 'anzeigen_einzeln',
      component: Entity,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/auflisten',
      name: 'auflisten',
      component: EntityListView,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/auflisten/:resourceType',
      name: 'auflisten_resource',
      component: EntityListView,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/berichte',
      name: 'media-projekt',
      component: Hello,
      meta: { auth: true, viewState: 0 }
    })
    routes.push({
      path: '/help',
      name: 'help',
      component: Help,
      meta: { auth: true, viewState: 0 }
    })
  }
  // will match before /public/:type?/:id?/:resourceType?
  // in case type === 'details'
  if (leurisMode === 'PORTAL') {
    routes.push({
      path: '/',
      name: 'welcome',
      component: Welcome
    })
    routes.push({
      path: '/welcome',
      name: 'welcome',
      component: Welcome
    })
    routes.push({
      path: '/details/:resourceType(forschungsprojekt|publikation|preis|kooperation|veranstaltung)/:resourceId',
      name: 'public_details',
      component: PublicEntity,
      props: true
    })
    routes.push({
      path: '/:type(ou|person)?/:id?/:resourceType?',
      name: 'public',
      component: Public,
      props: true
    })
  }

  // generic routes
  routes.push({
    path: '/404',
    name: '404',
    component: NotFound
  })
  routes.push({
    path: '*',
    name: 'notFound'
  })

  return routes
}

function activityFromPath (path) {
  let parts = path.split('/')
  return parts[parts.length - 1]
}

function createRouter (leurisMode) {
  const router = new Router({
    base: `/${leurisMode.toLowerCase()}/`,
    // https://router.vuejs.org/guide/essentials/history-mode.html
    mode: 'history',
    routes: createRoutes(leurisMode),
    linkActiveClass: 'is-active'
  })

  router.afterEach((to, from) => {
    if (from.name) {
      let behavior = 'smooth'
      let top = 223

      // when user logs out they are redirected to login page
      if (to.name === 'login') {
        top = 0
      }

      if (from.name === 'public' && to.name === 'public') {
        let fromActivity = activityFromPath(from.path)
        let toActivity = activityFromPath(to.path)
        // don't adjust viewport when switch tabs
        if (fromActivity !== toActivity) {
          return
        }
        top = 223
        behavior = 'smooth'
      }
      setTimeout(() => {
        window.scrollTo({
          top,
          left: 0,
          behavior
        })
      }, 300)
    }
  })

  router.beforeEach((to, from, next) => {
    // removeQueryParams is false by default
    let removeQueryParams = RouterQueryParameter.hasBlacklistedParam(to.query)
    // process passed query parameters
    if (to.query.lang) {
      // set language by query param
      switch (to.query.lang) {
        case 'de':
          store.dispatch('setLanguage', 'de_DE')
          break
        case 'en':
          store.dispatch('setLanguage', 'en_150')
          break
      }
      // delete lang query param
      delete to.query.lang
      // trigger route replace
      removeQueryParams = true
    }

    if (to.query.fis_persid) {
      store.dispatch('publicView/setSelected', {
        id: to.query.fis_persid,
        type: 'person',
        rootQueryType: 'person',
        typeLabel: 'Person'
      }).then(result => {
        if (result && result.redirect) {
          router.replace({ ...to, params: { ...to.params, id: result.orgaID } })
        }
        store.dispatch('publicView/reset')
        store.dispatch('publicView/loadAsyncData')
      })
      // delete fis_persid query param
      delete to.query.fis_persid
      // trigger route replace
      removeQueryParams = true
    }

    if (to.query.ou) {
      store.dispatch('publicView/setSelected', {
        id: to.query.ou,
        type: 'ou',
        rootQueryType: 'organisationalUnit',
        typeLabel: 'Organisationseinheit'
      }).then(result => {
        if (result && result.redirect) {
          router.replace({ ...to, params: { ...to.params, id: result.orgaID } })
        }
        store.dispatch('publicView/reset')
        store.dispatch('publicView/loadAsyncData')
      })
      // delete ou query param
      delete to.query.ou
      // trigger route replace
      removeQueryParams = true
    }

    let queryFilter = {}
    const availabeFilters = [
      'year',
      'status',
      'funding',
      'fundedByCategory',
      'inProject',
      'person',
      'freetext',
      'externalPartner',
      'category',
      'sdg',
    ]

    availabeFilters.forEach(filter => {
      if (to.params.resourceType && Object.keys(to.query).includes(filter)) {
        queryFilter[filter] = to.query[filter]
        delete to.query[filter]
        removeQueryParams = true
      }
    })

    // TODO: Review, might be better that queryPerPage is not passed (also since the selected is not passed)
    // let queryPerPage = null
    // if (to.params.resourceType && to.query.perPage) {
    //   queryPerPage = Number(to.query.perPage)
    //   delete to.query.perPage
    //   removeQueryParams = true
    // }

    let querySortOrder = null
    if (to.params.resourceType && to.query.sortOrder) {
      querySortOrder = to.query.sortOrder.toUpperCase()
      delete to.query.sortOrder
      removeQueryParams = true
    }

    let querySortField = null
    if (to.params.resourceType && to.query.sortField) {
      querySortField = to.query.sortField.toUpperCase()
      delete to.query.sortField
      removeQueryParams = true
    }

    if (removeQueryParams) {
      // reload without query params
      router.replace({
        ...to,
        query: RouterQueryParameter.clean(to.query)
      })
    }

    const authRequired = to.matched.some((route) => route.meta.auth)
    const authed = store.getters['user/isAuthorized']

    // set active view state 'internal view', 'public view' and 'public personal view'
    if (to.meta && to.query.profile) {
      store.dispatch('user/activeViewState', 2)
    } else if (to.name === 'start' && authed) {
      store.dispatch('user/activeViewState', 0)
    } else if (to.meta && to.meta.hasOwnProperty('viewState')) {
      store.dispatch('user/activeViewState', to.meta.viewState)
    } else {
      store.dispatch('user/activeViewState', 1)
    }

    if (to.hash.startsWith('#')) {
      console.log('Redirecting to hash-less URI')
      next({
        path: to.hash.slice(1)
      })
    } else if (!authed && to.name === 'start') {
      if (leurisMode === 'FOB') {
        next('/login')
      } else {
        next('/welcome')
      }
    } else if (authRequired && !authed) {
      next('/login')
    } else if (authed && to.name === 'login') {
      store.dispatch('user/activeViewState', 0)
      next({
        path: '/',
        replace: true
      })
    } else if (to.name === 'anzeigen_einzeln') {
      // TODO: Refactor to optional parameter
      const resourceSchema = schema[to.params.resourceType]
      store
        .dispatch('resource/setResource', {
          id: to.params.resourceId,
          type: to.params.resourceType,
          schema: resourceSchema
        }).then((data) => {
          next()
        })
    } else if (to.name === 'editieren') {
      // first check if a resource is being edited
      if (to.params.resourceType != null) {
        const resourceSchema = schema[to.params.resourceType]
        let resourceId = store.getters['editor/getId']
        let resourceType = store.getters['editor/getViewEntityType']
        let resourceData = store.getters['editor/getData']
        let resourceDataEmpty = (resourceData == null) || (Object.keys(resourceData).length === 0 && resourceData.constructor === Object)
        // only reload when actually changing route in order to keep current changes
        // BUT reload data if page is reloaded
        if (to.params.resourceType !== resourceType || to.params.resourceId !== resourceId || resourceDataEmpty) {
          if (store.getters['editor/isDirty']) {
            Dialog.confirm({
              message: router.app.$gettext('warnung.edit.nachricht'),
              confirmText: router.app.$gettext('warnung.edit.confirm'),
              cancelText: router.app.$gettext('warnung.edit.cancel'),
              onConfirm: () => {
                store
                  .dispatch('editor/setResource', {
                    id: to.params.resourceId,
                    type: to.params.resourceType,
                    schema: resourceSchema
                  }).then(() => {
                    next()
                  })
              },
              onCancel: () => {
                next(false)
              }
            })
          } else {
            store
              .dispatch('editor/setResource', {
                id: to.params.resourceId,
                type: to.params.resourceType,
                schema: resourceSchema
              }).then(() => {
                next()
              })
          }
        } else {
          next()
        }
      } else {
        next()
      }
    } else if (to.name === 'auflisten_resource') {
      store.dispatch('list/setResourceType', to.params.resourceType)
      next()
    } else if (to.name === 'welcome') {
      next({
        path: 'ou/1/forschungsprojekt',
        replace: true
      })
    } else if (to.name === 'public') {
      let fromSelected = store.getters['publicView/getSelected']
      // As default: id === 1 reset tree (this also set the name to "Universität Leipzig")
      // let treeSelected = store.getters['publicView/selected']
      if (to.params.id && Number(to.params.id) === 1 && to.params.type === 'ou') {
        store.dispatch('tree/reset')
        store.dispatch('tree/selectBranch', { id: to.params.id, name: 'Universität Leipzig' })
      } else {
        // set selected for tree component
        store.dispatch('tree/selectBranch', { id: to.params.id })
      }
      let validResourceType = false
      if (to.params.resourceType) {
        let resourceTypes = store.getters['publicView/resourceTypes']
        let tabIdx = resourceTypes.findIndex(resourceType => resourceType.type === to.params.resourceType)
        if (tabIdx !== -1) {
          store.dispatch('publicView/setActiveTab', tabIdx)
          validResourceType = true
        }
      }
      if (((from.params !== 'public') || ((fromSelected.type !== to.params.type) || fromSelected.id !== to.params.id))) {
        // show orga tree on organ view and the person search on person view
        store.dispatch('publicView/setShowOrgaTree', to.params.type === 'ou')

        if (to.params && to.params.id && to.params.type && validResourceType) {
          store.dispatch('publicView/setSelected', {
            id: to.params.id || 1, // if null load Universität as default
            type: to.params.type === 'person' ? 'person' : 'ou',
            rootQueryType: to.params.type === 'person' ? 'person' : 'organisationalUnit',
            typeLabel: to.params.type === 'person' ? 'Person' : 'Organisationseinheit'
          }).then(result => {
            if (result && result.redirect) {
              router.replace({ ...to, params: { ...to.params, id: result.orgaID } })
            }
            let dispatched = []
            if (Object.keys(queryFilter).length > 0 || querySortField != null || querySortOrder != null) {
              if (Object.keys(queryFilter).length > 0) {
                dispatched.push(store.dispatch('publicView/updateFilter', {
                  resourceType: to.params.resourceType,
                  updateFilter: queryFilter
                }))
              }
              if (querySortField != null) {
                dispatched.push(store.dispatch('publicView/setSortField', {
                  resourceType: to.params.resourceType,
                  sortField: querySortField
                }))
              }
              if (querySortOrder != null) {
                dispatched.push(store.dispatch('publicView/setSortOrder', {
                  resourceType: to.params.resourceType,
                  sortOrder: querySortOrder
                }))
              }
              // if (queryPerPage != null) {
              //   dispatched.push(store.dispatch('publicView/changePerPage', {
              //     resourceType: to.params.resourceType,
              //     perPage: Number(queryPerPage)
              //   }))
              // }

              Promise.all(dispatched).then(() => {
                store.dispatch('publicView/loadAsyncData')
                  .then(() => {
                    next()
                  })
                  .catch(() => {
                    next({
                      name: 'public',
                      params: {
                        type: 'ou',
                        id: 1,
                        resourceType: 'forschungsprojekt'
                      }
                    })
                  })
              })
            } else if ((fromSelected.type !== to.params.type) || (fromSelected.id !== to.params.id)) {
              // do not query the same data
              store.dispatch('publicView/reset').then(() => {
                store.dispatch('publicView/loadAsyncData')
                  .then(() => {
                    next()
                  })
                  .catch(() => {
                    next({
                      name: 'public',
                      params: {
                        type: 'ou',
                        id: 1,
                        resourceType: 'forschungsprojekt'
                      }
                    })
                  })
              })
              next()
            } else {
              next()
            }
          })
        } else if (!validResourceType) {
          console.error('404: Invalid Resource Type')
          // if given resource type is not one of available resourceTypes (getter),
          // display error page
          router.replace({name: '404'})
        } else if (!fromSelected.id) {
          // if no id was set query data
          store.dispatch('publicView/setSelected', {
            id: to.params.id || 1, // if null load Universität as default
            type: to.params.type === 'person' ? 'person' : 'ou',
            rootQueryType: to.params.type === 'person' ? 'person' : 'organisationalUnit',
            typeLabel: to.params.type === 'person' ? 'Person' : 'Organisationseinheit'
          }).then(() => {
            store.dispatch('publicView/reset')
            store.dispatch('publicView/loadAsyncData')
            next()
          })
        } else {
          next()
        }
      } else {
        store.dispatch('publicView/reset')
        store.dispatch('publicView/loadAsyncData')
        next()
      }
    } else if (to.name === 'notFound') {
      console.error('404: Route does not exist')
      router.replace({name: '404'})
    } else {
      next()
    }
  })
  return router
}

export default createRouter



// WEBPACK FOOTER //
// ./src/router/index.js